import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { Trans } from '@lingui/react';
import { i18n } from '@lingui/core';
import React, { useState, useEffect, Suspense } from 'react';
import styled from 'styled-components';
import { Box, useBreakpoint, useDisclosure, DropdownMenu, Pin, Tooltip, Avatar, Media, Paragraph, List, callAllHandlers, ProfileOutlined, Text, PackOutlined, CaretRightOutlined, CaretLeftOutlined, CdArrowOutlined, PercentageOutlined, ProductSamplesOutlined, ProductPluginsOutlined, ProductNetworkOutlined, FileTextOutlined, UserMessageOutlined, QuoteOutlined, CogOutlined, HelpOutlined, GiftOutlined, CouponOutlined, ComputerDesktopOutlined, LeaveOutlined } from '@landr/maestro';
import { palette, radius } from '@landr/maestro-legacy';
import { useApolloClient } from '@apollo/client';
import { addLandrEventListener, LandrEventType, isDesktopApp, getNetworkLink, getSamplesLink, getSupportLink } from '@landr/core';
import { getInitials } from '../../../../../../utils/initials.es.js';
import { openNetworkLink, openWebAppLink, getWebAppLink, openSamplesLink, openPluginsLink, getPluginsLink, openSupportLink, openRedeemAProductLink, getRedeemAProductLink } from '../../../../../../utils/links.es.js';
import { useLinkClickHandler } from '../../../../hooks/useLinkClickHandler.es.js';
import { useFeedbackModal } from '../NavBarFeedbackModal/FeedbackModalProvider.es.js';
import { usePrivateProfileQuery } from './hooks/usePrivateProfileQuery.es.js';
import { ProfileHiddenTooltip } from './components/ProfileHiddenTooltip/ProfileHiddenTooltip.es.js';
import { ProfileCompletion } from './components/ProfileCompletion/ProfileCompletion.es.js';
import { NavBarSearch } from '../NavBarSearch/NavBarSearch.es.js';
import { updateProfileCompletionCache } from './components/ProfileCompletion/hooks/useProfileCompletionQuery.es.js';
import { useNavBarContext } from '../../../../../../contexts/NavBarProvider/NavBarContext.es.js';
import { Links } from '../../../../../../enums/Links.es.js';

var _templateObject, _templateObject2;
var UnstyledButton = styled('button')(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    cursor: pointer;\n    position: relative;\n    padding: 0;\n"])));

/*
    Maestro's Badge doesn't fit the design requirements, and does not work with
        DropdownMenu.Trigger + Tooltip (Tooltip popover will not trigger)
*/
var StyledCustomAvatarBadge = styled(Box)(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    position: absolute;\n    background-color: ", ";\n    border-radius: ", ";\n    width: 8px;\n    height: 8px;\n    bottom: ", ";\n    right: ", ";\n    outline: 2px solid ", ";\n"])), palette('neutral.50'), radius('round'), function (_ref) {
  var isMobile = _ref.isMobile;
  return isMobile ? '12%' : '15%';
}, function (_ref2) {
  var isMobile = _ref2.isMobile;
  return isMobile ? '12%' : '4%';
}, palette('neutral.900'));
var NetworkAvailability = React.lazy(function () {
  return import('../NetworkAvailability/index.es.js').then(function (module) {
    return {
      "default": module.NetworkAvailability
    };
  });
});
var NavBarUserMenu = function NavBarUserMenu() {
  var isMobile = useBreakpoint('md');
  var isTablet = useBreakpoint('lg');
  var _useNavBarContext = useNavBarContext(),
    user = _useNavBarContext.user,
    env = _useNavBarContext.env,
    onLogout = _useNavBarContext.onLogout,
    language = _useNavBarContext.language;
  var _useDisclosure = useDisclosure(),
    isOpen = _useDisclosure.isOpen,
    onOpen = _useDisclosure.onOpen,
    onClose = _useDisclosure.onClose;
  var handleLinkClick = useLinkClickHandler();
  var apolloClient = useApolloClient();
  var _usePrivateProfileQue = usePrivateProfileQuery({
      userId: user === null || user === void 0 ? void 0 : user.userId
    }),
    privateProfileQueryData = _usePrivateProfileQue.data;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    userAvatarUrl = _useState2[0],
    setUserAvatarUrl = _useState2[1];
  var _useFeedbackModal = useFeedbackModal(),
    openModal = _useFeedbackModal.openModal;
  useEffect(function () {
    var callback = function callback(data) {
      updateProfileCompletionCache(apolloClient.cache, data);
    };
    var unsubscribe = addLandrEventListener(LandrEventType.UserPublicProfileUpdate, callback);
    return unsubscribe;
  }, [apolloClient]);
  useEffect(function () {
    var callback = function callback(data) {
      setUserAvatarUrl(data.avatarUrl);
    };
    var unsubscribe = addLandrEventListener(LandrEventType.UserAvatarUpdate, callback);
    return unsubscribe;
  }, [apolloClient]);
  useEffect(function () {
    if (!userAvatarUrl) {
      setUserAvatarUrl(user === null || user === void 0 ? void 0 : user.profilePicture72Url);
    }
  }, [user === null || user === void 0 ? void 0 : user.profilePicture72Url, userAvatarUrl]);
  var handleOnLogout = function handleOnLogout() {
    return onLogout === null || onLogout === void 0 ? void 0 : onLogout();
  };
  var handleOnNavigateToProfile = function handleOnNavigateToProfile() {
    return openNetworkLink(env, '/users/me');
  };
  var isInDesktopApp = isDesktopApp();
  return /*#__PURE__*/React.createElement(DropdownMenu, {
    placement: 'bottom-end',
    offset: [0, 8],
    isOpen: isOpen,
    onOpen: onOpen,
    onClose: onClose
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignContent: "center",
    px: isMobile ? 'md' : 'sm'
  }, /*#__PURE__*/React.createElement(Pin, {
    component: (privateProfileQueryData === null || privateProfileQueryData === void 0 ? void 0 : privateProfileQueryData.isProfilePrivate) && /*#__PURE__*/React.createElement(Tooltip, {
      content: i18n._(
      /*i18n*/
      {
        id: "TdW6jm"
      }),
      position: "top-end"
    }, /*#__PURE__*/React.createElement(StyledCustomAvatarBadge, {
      isMobile: isMobile
    })),
    placement: "bottom-right"
  }, /*#__PURE__*/React.createElement(DropdownMenu.Trigger, null, /*#__PURE__*/React.createElement(UnstyledButton, null, /*#__PURE__*/React.createElement(Avatar, {
    "data-e2e-id": "nav.avatar",
    alt: i18n._(
    /*i18n*/
    {
      id: "E/uzM2"
    }),
    size: isMobile ? 'xs' : 'sm',
    src: userAvatarUrl,
    fallback: getInitials(user)
  }))))), /*#__PURE__*/React.createElement(DropdownMenu.List, {
    drawerBreakpoint: "lg",
    maxHeight: "85vh",
    overflow: "auto",
    width: isTablet ? undefined : 280,
    maxWidth: isTablet ? undefined : 280
  }, /*#__PURE__*/React.createElement(Media, {
    px: "sm",
    py: "sm"
  }, /*#__PURE__*/React.createElement(Media.Image, null, /*#__PURE__*/React.createElement(Avatar, {
    src: userAvatarUrl,
    fallback: getInitials(user)
  })), /*#__PURE__*/React.createElement(Media.Body, {
    maxWidth: "100%",
    overflow: "hidden"
  }, /*#__PURE__*/React.createElement(Paragraph, {
    size: "lg",
    fontWeight: "bold",
    color: "neutral.0",
    m: "0",
    nbOfLines: 1
  }, user === null || user === void 0 ? void 0 : user.firstname, " ", user === null || user === void 0 ? void 0 : user.lastname), /*#__PURE__*/React.createElement(Paragraph, {
    size: "sm",
    contrast: "low",
    m: "0",
    nbOfLines: 1
  }, user === null || user === void 0 ? void 0 : user.email))), /*#__PURE__*/React.createElement(List.Divider, null), /*#__PURE__*/React.createElement(Suspense, {
    fallback: null
  }, /*#__PURE__*/React.createElement(NetworkAvailability, null)), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(handleLinkClick(openNetworkLink, env, Links.MyProfile)),
    href: getNetworkLink(env, Links.MyProfile)
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(ProfileOutlined, null)), /*#__PURE__*/React.createElement(Text, {
    mr: "xxs"
  }, /*#__PURE__*/React.createElement(Trans, {
    id: "vERlcd"
  })), /*#__PURE__*/React.createElement(ProfileHiddenTooltip, {
    isProfilePrivate: !!(privateProfileQueryData === null || privateProfileQueryData === void 0 ? void 0 : privateProfileQueryData.isProfilePrivate),
    onCtaClick: handleOnNavigateToProfile
  }), /*#__PURE__*/React.createElement(List.ItemSecondaryAction, null, /*#__PURE__*/React.createElement(ProfileCompletion, null))), /*#__PURE__*/React.createElement(DropdownMenu, {
    triggerEvent: isTablet ? 'click' : 'hover',
    placement: "right-start",
    offset: [-8, 8],
    usePortal: false
  }, /*#__PURE__*/React.createElement(DropdownMenu.Trigger, null, /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "button",
    closeOnClick: false
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(PackOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "DKqHj3"
  }), /*#__PURE__*/React.createElement(DropdownMenu.SecondaryAction, null, /*#__PURE__*/React.createElement(CaretRightOutlined, null)))), /*#__PURE__*/React.createElement(DropdownMenu.List, {
    drawerBreakpoint: "lg"
  }, isTablet && /*#__PURE__*/React.createElement(DropdownMenu.Item, null, /*#__PURE__*/React.createElement(DropdownMenu.Icon, null, /*#__PURE__*/React.createElement(CaretLeftOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "iH8pgl"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openWebAppLink, env, Links.Library)),
    href: getWebAppLink(env, Links.Library)
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(PackOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "6a9S3U"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Divider, null), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openWebAppLink, env, Links.ReleasesList)),
    href: getWebAppLink(env, Links.ReleasesList)
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(CdArrowOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "emlOcb"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openWebAppLink, env, Links.ReleasesCatalog)),
    href: getWebAppLink(env, Links.ReleasesCatalog)
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(PercentageOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "MXCwiB"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Divider, null), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openSamplesLink, env, Links.SamplesDownloads)),
    href: getSamplesLink(env, Links.SamplesDownloads)
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(ProductSamplesOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "ABGDkz"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openPluginsLink, env, "".concat(Links.MyPlugins))),
    href: getPluginsLink(env, "".concat(Links.MyPlugins))
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(ProductPluginsOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "KZ1GxU"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Divider, null), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openNetworkLink, env, Links.Connections)),
    href: getNetworkLink(env, Links.Connections)
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(ProductNetworkOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "2ToYYt"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openWebAppLink, env, Links.Orders)),
    href: getWebAppLink(env, Links.Orders)
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(FileTextOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "bnKr2F"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openWebAppLink, env, Links.Assignments)),
    href: getWebAppLink(env, Links.Assignments)
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(UserMessageOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "45ZYJb"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openWebAppLink, env, Links.Quotes)),
    href: getWebAppLink(env, Links.Quotes)
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(QuoteOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "ooAdRg"
  })))), /*#__PURE__*/React.createElement(DropdownMenu, {
    triggerEvent: isTablet ? 'click' : 'hover',
    placement: "right-start",
    offset: [-8, 8],
    usePortal: false
  }, /*#__PURE__*/React.createElement(DropdownMenu.Trigger, null, /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "button",
    closeOnClick: false
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(CogOutlined, null)), /*#__PURE__*/React.createElement(Box, {
    sx: {
      whiteSpace: 'nowrap'
    }
  }, /*#__PURE__*/React.createElement(Trans, {
    id: "Ttx8pE"
  })), /*#__PURE__*/React.createElement(DropdownMenu.SecondaryAction, null, /*#__PURE__*/React.createElement(CaretRightOutlined, null)))), /*#__PURE__*/React.createElement(DropdownMenu.List, {
    drawerBreakpoint: "lg"
  }, isTablet && /*#__PURE__*/React.createElement(DropdownMenu.Item, null, /*#__PURE__*/React.createElement(DropdownMenu.Icon, null, /*#__PURE__*/React.createElement(CaretLeftOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "iH8pgl"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openWebAppLink, env, Links.AccountGeneralSettings)),
    href: getWebAppLink(env, Links.AccountGeneralSettings)
  }, /*#__PURE__*/React.createElement(Trans, {
    id: "Ye6I4I"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openWebAppLink, env, Links.AccountPlansAndCredits)),
    href: getWebAppLink(env, Links.AccountPlansAndCredits)
  }, /*#__PURE__*/React.createElement(Trans, {
    id: "rWqg8N"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openWebAppLink, env, Links.AccountBilling)),
    href: getWebAppLink(env, Links.AccountBilling)
  }, /*#__PURE__*/React.createElement(Trans, {
    id: "R+w/Va"
  })))), /*#__PURE__*/React.createElement(DropdownMenu, {
    triggerEvent: isTablet ? 'click' : 'hover',
    placement: "right-start",
    offset: [-8, 8],
    usePortal: false
  }, /*#__PURE__*/React.createElement(DropdownMenu.Trigger, null, /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "button",
    closeOnClick: false
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(HelpOutlined, null)), /*#__PURE__*/React.createElement(Box, {
    sx: {
      whiteSpace: 'nowrap'
    }
  }, /*#__PURE__*/React.createElement(Trans, {
    id: "wTXUdD"
  })), /*#__PURE__*/React.createElement(DropdownMenu.SecondaryAction, null, /*#__PURE__*/React.createElement(CaretRightOutlined, null)))), /*#__PURE__*/React.createElement(DropdownMenu.List, {
    drawerBreakpoint: "lg"
  }, isTablet && /*#__PURE__*/React.createElement(DropdownMenu.Item, null, /*#__PURE__*/React.createElement(DropdownMenu.Icon, null, /*#__PURE__*/React.createElement(CaretLeftOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "iH8pgl"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openSupportLink, language)),
    href: getSupportLink(language)
  }, /*#__PURE__*/React.createElement(Trans, {
    id: "/lDBHm"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    onClick: callAllHandlers(onClose, openModal)
  }, /*#__PURE__*/React.createElement(Trans, {
    id: "myOJYg"
  })), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(onClose, handleLinkClick(openSupportLink, language, "/requests/new")),
    href: getSupportLink(language, "/requests/new")
  }, /*#__PURE__*/React.createElement(Trans, {
    id: "mpby9d"
  })))), /*#__PURE__*/React.createElement(List.Divider, null), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(handleLinkClick(openWebAppLink, env, Links.Referrals)),
    href: getWebAppLink(env, Links.Referrals)
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(GiftOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "KSBmDv"
  })), isMobile && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(NavBarSearch, null)), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(handleLinkClick(openRedeemAProductLink, env)),
    href: getRedeemAProductLink(env)
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(CouponOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "/8lAG8"
  })), !isInDesktopApp && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    as: "a",
    onClick: callAllHandlers(handleLinkClick(openPluginsLink, env, "".concat(Links.DesktopAppInstallerPage))),
    href: getPluginsLink(env, "".concat(Links.DesktopAppInstallerPage))
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(ComputerDesktopOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "i74xio"
  })), /*#__PURE__*/React.createElement(List.Divider, null), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    "data-e2e-id": "nav.logout-button",
    onClick: handleOnLogout
  }, /*#__PURE__*/React.createElement(List.ItemIcon, null, /*#__PURE__*/React.createElement(LeaveOutlined, null)), /*#__PURE__*/React.createElement(Trans, {
    id: "FgAxTj"
  }))));
};

export { NavBarUserMenu };
